(function () {
  const WIDGET_SELECTOR = '.delivery-estimate-widget';
  const INITIALISED_ATTR = 'data-delivery-widget-ready';
  const HARD_CODED_DVSA_READ_MORE_URL = '/pages/full-throttle-ebikes-by-pedibal';

  async function getBankHolidays(region) {
    try {
      const res = await fetch('https://www.gov.uk/bank-holidays.json');
      if (!res.ok) throw new Error('Bank holiday fetch failed');

      const data = await res.json();
      const events = data[region] && data[region].events ? data[region].events : [];

      return new Set(events.map(event => event.date));
    } catch (error) {
      console.warn('Could not load bank holidays', error);
      return new Set();
    }
  }

  function toISODateLondon(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);

    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;

    return `${year}-${month}-${day}`;
  }

  function getLondonParts(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);

    const get = (type) => parts.find(p => p.type === type)?.value || '';

    return {
      weekday: get('weekday'),
      monthName: get('month'),
      day: parseInt(get('day'), 10),
      hour: parseInt(get('hour'), 10),
      minute: parseInt(get('minute'), 10)
    };
  }

  function ordinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  function formatDateLabel(date) {
    const p = getLondonParts(date);
    return `${p.weekday}, ${p.day}${ordinalSuffix(p.day)} ${p.monthName}`;
  }

  function isWeekendLondon(date) {
    const weekday = getLondonParts(date).weekday;
    return weekday === 'Saturday' || weekday === 'Sunday';
  }

  function addDays(date, days) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }

  function isWorkingDay(date, bankHolidaySet) {
    return !isWeekendLondon(date) && !bankHolidaySet.has(toISODateLondon(date));
  }

  function addWorkingDays(date, numberOfDays, bankHolidaySet) {
    let d = new Date(date);
    let added = 0;

    while (added < numberOfDays) {
      d = addDays(d, 1);

      if (isWorkingDay(d, bankHolidaySet)) {
        added++;
      }
    }

    return d;
  }

  function getDispatchDate(now, cutoffHour, cutoffMinute, bankHolidaySet) {
    const london = getLondonParts(now);

    const beforeCutoff =
      isWorkingDay(now, bankHolidaySet) &&
      (
        london.hour < cutoffHour ||
        (london.hour === cutoffHour && london.minute < cutoffMinute)
      );

    if (beforeCutoff) {
      return now;
    }

    let d = addDays(now, 1);

    while (!isWorkingDay(d, bankHolidaySet)) {
      d = addDays(d, 1);
    }

    return d;
  }

  function getCountdown(now, cutoffHour, cutoffMinute, bankHolidaySet) {
    if (!isWorkingDay(now, bankHolidaySet)) return null;

    const london = getLondonParts(now);
    const minutesNow = london.hour * 60 + london.minute;
    const cutoffTotal = cutoffHour * 60 + cutoffMinute;

    if (minutesNow >= cutoffTotal) return null;

    const diff = cutoffTotal - minutesNow;
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  function normaliseNumber(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;

    const number = parseInt(value, 10);
    return Number.isNaN(number) ? fallback : number;
  }

  function isThrottleVariant(variant) {
    if (!variant || !variant.title) return false;

    const title = variant.title.toLowerCase();

    return (
      title.includes('throttle') ||
      title.includes('dvsa') ||
      title.includes('certified')
    );
  }

  function getProductFormForWidget(widget) {
    return (
      widget
        .closest('product-info, product-rerender, section, .shopify-section')
        ?.querySelector('form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]')
    );
  }

  function getCurrentVariant(widget, variants) {
    const form = getProductFormForWidget(widget);
    const fallbackId = widget.dataset.selectedVariantId;

    if (!form) {
      return variants.find(v => String(v.id) === String(fallbackId)) || variants[0];
    }

    const variantInput = form.querySelector('input[name="id"], select[name="id"]');
    const variantId = variantInput ? variantInput.value : fallbackId;

    return variants.find(v => String(v.id) === String(variantId)) || variants[0];
  }

  function getLeadTimeLine(extraWorkingDays, countdown, countdownPrefix, afterCutoffText) {
    if (extraWorkingDays === 0 && countdown) {
      return `${countdownPrefix}${countdown}`;
    }

    if (extraWorkingDays === 0 && !countdown) {
      return afterCutoffText;
    }

    const maxWorkingDays = extraWorkingDays * 2;

    return `Usually ${extraWorkingDays}–${maxWorkingDays} working days`;
  }

  function renderWidget(widget, bankHolidaySet, variants) {
    const cutoffHour = normaliseNumber(widget.dataset.cutoffHour, 14);
    const cutoffMinute = normaliseNumber(widget.dataset.cutoffMinute, 0);

    const beforeTemplate = widget.dataset.deliveryLabelBefore || '🚚 Get it by {date}';
    const afterTemplate = widget.dataset.deliveryLabelAfter || '📦 Expected delivery by {date}';
    const countdownPrefix = widget.dataset.countdownPrefix || '⏰ Order within ';
    const afterCutoffText = widget.dataset.afterCutoffText || 'Next business day dispatch';

    const pedalNote =
      widget.dataset.pedalNote ||
      'Pedal Assist gives you motor assistance up to 15.5 mph while pedalling.';

    const throttleEarlyNote =
      widget.dataset.throttleEarlyNote ||
      'The delivery date above reflects our pre-tested DVSA-certified bike availability. Due to high customer demand, we pre-test many bikes in advance during peak season, so customers often avoid the usual 2–5 week wait for individual approval and certification.';

    const throttleWaitNote =
      widget.dataset.throttleWaitNote ||
      'This throttle option usually takes 2–5 weeks because each bike requires individual DVSA approval and certification.';

    const throttleBenefit =
      widget.dataset.throttleBenefit ||
      'Throttle DVSA Certified gives you power up to 15.5 mph without pedalling, plus normal pedal assist, while allowing fully legal 15.5 mph throttle use on UK roads with no insurance, driving licence, MOT or reg plate required.';

    const currentVariant = getCurrentVariant(widget, variants) || {};
    const extraWorkingDays = normaliseNumber(currentVariant.extra_working_days, 0);
    const throttleSelected = isThrottleVariant(currentVariant);

    const now = new Date();
    const dispatchDate = getDispatchDate(now, cutoffHour, cutoffMinute, bankHolidaySet);

    const baseDeliveryWorkingDays = 1;
    const workingDaysToDelivery = baseDeliveryWorkingDays + extraWorkingDays;

    const deliveryDate = addWorkingDays(dispatchDate, workingDaysToDelivery, bankHolidaySet);

    const countdown =
      extraWorkingDays === 0
        ? getCountdown(now, cutoffHour, cutoffMinute, bankHolidaySet)
        : null;

    const useWeeksTitle = throttleSelected && extraWorkingDays >= 14;

    const line1 = useWeeksTitle
      ? '📦 Expected delivery in 2–5 weeks'
      : countdown
        ? beforeTemplate.replace('{date}', formatDateLabel(deliveryDate))
        : extraWorkingDays > 0
          ? `📦 Expected delivery from ${formatDateLabel(deliveryDate)}`
          : afterTemplate.replace('{date}', formatDateLabel(deliveryDate));

    const line2 = useWeeksTitle
      ? 'Throttle DVSA approval and certification required'
      : getLeadTimeLine(
          extraWorkingDays,
          countdown,
          countdownPrefix,
          afterCutoffText
        );

    let noteText = '';
    let explainerText = '';

    if (throttleSelected) {
      noteText = extraWorkingDays < 14 ? throttleEarlyNote : throttleWaitNote;
      explainerText = throttleBenefit;
    } else {
      noteText = '';
      explainerText = pedalNote;
    }

    const readMoreHtml =
      throttleSelected
        ? ` <a href="${HARD_CODED_DVSA_READ_MORE_URL}">Read more</a>`
        : '';

    const noteHtml = noteText
      ? `
        <div class="delivery-estimate-box__note">
          ${noteText}${readMoreHtml}
        </div>
      `
      : '';

    const explainerHtml = explainerText
      ? `
        <div class="delivery-estimate-box__explainer">
          <div class="delivery-estimate-box__explainer-title">Options explained:</div>
          <div class="delivery-estimate-box__explainer-text">${explainerText}</div>
        </div>
      `
      : '';

    widget.innerHTML = `
      <div class="delivery-estimate-box__inner">
        <div class="delivery-estimate-box__line1">${line1}</div>
        <div class="delivery-estimate-box__line2">${line2}</div>
        ${noteHtml}
        ${explainerHtml}
      </div>
    `;
  }

  async function initWidget(widget) {
    if (!widget || widget.hasAttribute(INITIALISED_ATTR)) return;

    widget.setAttribute(INITIALISED_ATTR, 'true');

    let variants = [];

    try {
      variants = JSON.parse(widget.dataset.variants || '[]');
    } catch (e) {
      console.warn('Could not parse delivery variant data', e);
    }

    const region = widget.dataset.region || 'england-and-wales';
    const bankHolidaySet = await getBankHolidays(region);

    const render = () => renderWidget(widget, bankHolidaySet, variants);

    render();

    const timer = setInterval(() => {
      if (!document.body.contains(widget)) {
        clearInterval(timer);
        return;
      }

      render();
    }, 60000);

    const form = getProductFormForWidget(widget);

    if (form) {
      let lastVariantId =
        form.querySelector('input[name="id"], select[name="id"]')?.value || '';

      const watchVariantInput = () => {
        const currentId =
          form.querySelector('input[name="id"], select[name="id"]')?.value || '';

        if (currentId !== lastVariantId) {
          lastVariantId = currentId;
          render();
        }
      };

      form.addEventListener('change', () => {
        setTimeout(watchVariantInput, 80);
      });

      form.addEventListener('input', () => {
        setTimeout(watchVariantInput, 80);
      });

      const variantInput = form.querySelector('input[name="id"], select[name="id"]');

      if (variantInput) {
        const inputObserver = new MutationObserver(() => {
          watchVariantInput();
        });

        inputObserver.observe(variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }
  }

  function initAllWidgets() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(initWidget);
  }

  document.addEventListener('DOMContentLoaded', initAllWidgets);
  document.addEventListener('shopify:section:load', initAllWidgets);

  const pageObserver = new MutationObserver(() => {
    initAllWidgets();
  });

  pageObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();